import React, {
    createContext,
    useContext,
    useState,
    useCallback,
    useMemo,
    useEffect,
} from "react";
import { STRINGS, LANGS } from "@/lib/i18n";
import "@/lib/i18nLanding";
import "@/lib/i18nCatalog";
import "@/lib/i18nDashboard";

const LANG_KEY = "vt_lang";
const VALID = new Set(LANGS.map((l) => l.code));

/**
 * Multi-language plumbing.
 *
 * Priority (highest → lowest):
 *   1. ?lang=xx in URL — always wins (used by shareable localized links)
 *   2. localStorage(vt_lang) — user's previous explicit choice
 *   3. navigator.language — browser locale on first visit
 *   4. "en" — final fallback
 *
 * The active language is reflected on <html lang="…"> so Indic font-family
 * pairing kicks in via :lang() selectors in index.css.
 */
function detectBrowserLang() {
    if (typeof navigator === "undefined") return "en";
    const raw = (navigator.language || navigator.userLanguage || "en").toLowerCase();
    const primary = raw.split("-")[0];
    return VALID.has(primary) ? primary : "en";
}

function readUrlLang() {
    if (typeof window === "undefined") return null;
    try {
        const p = new URLSearchParams(window.location.search);
        const q = (p.get("lang") || "").toLowerCase();
        if (VALID.has(q)) return q;
    } catch {
        /* ignore */
    }
    return null;
}

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
    const [lang, setLangState] = useState(() => {
        const urlLang = readUrlLang();
        if (urlLang) {
            try { localStorage.setItem(LANG_KEY, urlLang); } catch { /* quota */ }
            return urlLang;
        }
        const saved = localStorage.getItem(LANG_KEY);
        if (saved && VALID.has(saved)) return saved;
        const detected = detectBrowserLang();
        try { localStorage.setItem(LANG_KEY, detected); } catch { /* quota */ }
        return detected;
    });

    useEffect(() => {
        try { document.documentElement.setAttribute("lang", lang || "en"); } catch { /* SSR safety */ }
    }, [lang]);

    // Keep tab in sync if user changes language in another tab.
    useEffect(() => {
        const onStorage = (e) => {
            if (e.key === LANG_KEY && e.newValue && VALID.has(e.newValue)) {
                setLangState(e.newValue);
            }
        };
        window.addEventListener("storage", onStorage);
        return () => window.removeEventListener("storage", onStorage);
    }, []);

    const setLang = useCallback((code) => {
        if (!VALID.has(code)) return;
        try { localStorage.setItem(LANG_KEY, code); } catch { /* quota */ }
        setLangState(code);
        // Update ?lang= query param without reloading the page (shareable URLs)
        try {
            const url = new URL(window.location.href);
            if (code === "en") {
                url.searchParams.delete("lang");
            } else {
                url.searchParams.set("lang", code);
            }
            window.history.replaceState({}, "", url.toString());
        } catch { /* ignore */ }
    }, []);

    const t = useCallback(
        (key) => STRINGS[lang]?.[key] ?? STRINGS.en[key] ?? key,
        [lang]
    );

    const value = useMemo(() => ({ lang, setLang, t, langs: LANGS }), [lang, setLang, t]);

    return (
        <LanguageContext.Provider value={value}>
            {/* Remount on language change so hook-free tr() consumers re-render too. */}
            <div key={lang} className="contents">{children}</div>
        </LanguageContext.Provider>
    );
}

export function useLang() {
    const ctx = useContext(LanguageContext);
    if (!ctx) throw new Error("useLang must be used within LanguageProvider");
    return ctx;
}
